import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function AdminDashboard({ user, token, onBack, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/user/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete user');
      }

      setSuccess('User deleted successfully');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMakeAdmin = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/make-admin/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to make user admin');
      }

      setSuccess('User is now an admin');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to remove admin privileges from this user?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/remove-admin/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove admin');
      }

      setSuccess('Admin privileges removed');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const adminCount = users.filter(u => u.role === 'admin').length;

  const getInitials = (name) => {
    return (name || 'U')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2) || 'U';
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-header-content">
          <button className="back-btn" onClick={onBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Products
          </button>
          <h1>Admin Dashboard</h1>
          <div className="admin-user-info">
            <span>{user.name}</span>
            <button className="logout-btn" onClick={onLogout}>Logout</button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')}>x</button>
          </div>
        )}

        {success && (
          <div className="success-message">{success}</div>
        )}

        <h2 style={{ marginBottom: '16px', fontSize: '16px', color: '#666' }}>
          All Users ({users.length})
        </h2>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <div className="users-list">
            {users.map(u => (
              <div key={u.id} className={`user-card ${u.role === 'admin' ? 'is-admin' : ''}`}>
                <div className="user-avatar">
                  {getInitials(u.name)}
                  {u.role === 'admin' && <span className="admin-badge">Admin</span>}
                </div>
                <div className="user-details">
                  <h3>{u.name}</h3>
                  <p className="user-email">{u.email}</p>
                  <div className="user-meta">
                    <span>Employee ID: {u.employeeId}</span>
                    <span>Dept: {u.department}</span>
                    <span>Location: {u.location}</span>
                  </div>
                  <div className="user-meta">
                    <span>Designation: {u.designation}</span>
                  </div>
                </div>
                <div className="user-actions">
                  {u.id !== user.id && (
                    <>
                      {u.role !== 'admin' && adminCount < 3 && (
                        <button className="admin-btn" onClick={() => handleMakeAdmin(u.id)}>
                          Make Admin
                        </button>
                      )}
                      {u.role === 'admin' && (
                        <button className="remove-admin-btn" onClick={() => handleRemoveAdmin(u.id)}>
                          Remove Admin
                        </button>
                      )}
                      <button className="delete-btn" onClick={() => handleDelete(u.id)}>
                        Delete
                      </button>
                    </>
                  )}
                  {u.id === user.id && (
                    <span className="you-badge">You</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;
